import React, { useEffect, useMemo, useState } from 'react'
import { createSearchParams, NavLink, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Breadcrumb, Col, Container, Row, Spinner, Tab } from 'react-bootstrap'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { useLazyGetUserRoomSessionsQuery } from '../../apps/professional-app/fa/store/fa'
import { RoomSessionList } from '../../apps/professional-app/fa/PastMeetings/PastMeetings'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { useGetExternalUserByIdQuery } from '../../store/externalUser/externalUser'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetAllUsersQuery, useGetMeQuery } from '../../store/user/user'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { CustomPaginationComponent } from '../../components/DataTable/CustomPaginationComponent'
import buttonStyles from '../../components/Button/Button.module.scss'
import { ConsentModal } from '../../components/Consent/ConsentModal'
import { ConsentIcon } from '../../components/Consent/ConsentIcon'
import { useConsentManualConfigs } from '../../components/Consent/useConsentManualConfigs'
import { useZocksAuth, useZocksQuota } from '../../components/ScopedBlock/hooks'
import { Roles } from '../../components/Roles/Roles'
import { ContactMeetingPrepModal } from '../AIAgent/ContactMeetingPrep'
import { AIAssistantDropdown } from '../AIAgent/AIAssistantDropdown'
import { AskAnythingContactModal } from './AskAnythingContactModal'
import { EditContactModal } from './EditContactModal'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { Button } from '../../components/Button/Button'
import { Tabs } from '../../components/Tabs/Tabs'
import { ClientProfile } from '../ClientProfile/ClientProfile'
import PrizeBadge from '../../components/ScopedBlock/PrizeBadge.svg?url'
import { FormFillModal } from '../AIAgent/Form/FormFillModal'
import { ClientProfileNotAvailableModal } from './ClientProfileNotAvailableModal'
import { ContactAnalytics } from './ContactAnalytics'
import { ContactEmailsV2 } from '../EmailV2/ContactEmailsV2'
import './Contact.scss'
import { ContactTasks } from './ContactTasks/ContactTasks'
import classNames from 'classnames'
import { setFormFillStartedFrom } from '../../store/meetingSummary/forms'
import { useLanguageOptions } from '../../common/languageUtils'
import { ContactBusinessEvents } from './ContactBusinessEvents/ContactBusinessEvents'
import { ContactEmailResponseModal } from './ContactEmailResponseModal/ContactEmailResponseModal'
import { ContactDocuments } from '../ProcessDocument/Documents/ContactDocuments'

export const Contact: React.FC = () => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const { getContactLabel } = useLanguageOptions()
  const dispatch = useAppDispatch()

  const { data: currentUser } = useGetMeQuery({ accountId })
  const emailIntegrations = useMemo(() => {
    return currentUser?.integrations?.filter((i) => i.type === 'mail') ?? []
  }, [currentUser])
  const navigate = useNavigate()
  const { activeTab = 'pastMeetings' } = useParams()
  const [selectedTab, setSelectedTab] = useState(activeTab)

  const { hasQuota } = useZocksQuota()

  const consentTrackingAvailable = hasQuota('ai.userConsentTracking.value')
  const emailEnabled = hasQuota('integration.emailEnabled.value')
  const clientProfileAvailable = hasQuota('ai.clientProfile.value')
  const formEnabled = hasQuota('ai.formFill.value')
  const documentSummaryEnabled = hasQuota('ai.documentSummaryAgent.value')
  const businessEventsEnabled = hasQuota('ai.businessEvent.value')

  const { userId = '' } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const [triggerGetRoomSessions, { data: roomSessions, isFetching: roomSessionsLoading }] = useLazyGetUserRoomSessionsQuery()
  const {
    data: contactData,
    isLoading: isLoadingUserData,
    refetch: refetchContact,
  } = useGetExternalUserByIdQuery({ accountId, userId }, { refetchOnMountOrArgChange: true, skip: !userId })

  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const { data: users } = useGetAllUsersQuery({
    accountId: accountId,
    identityTypes: ['idp'],
  })

  const contactOwner = contactData && contactData.data.owner ? users?.data.find((u) => u.id === contactData.data.owner?.ownerId) : null

  const [paginationPage, setPaginationPage] = useState(1)
  const [paginationPageSize, setPaginationPageSize] = useState(100)
  const [showEditContactModal, setShowEditContactModal] = useState<boolean>(false)
  const [showContactMeetingPrepModal, setShowContactMeetingPrepModal] = useState<boolean>(false)
  const [showAskAnythingModal, setShowAskAnythingModal] = useState<boolean>(false)
  const [showEmailResponseModal, setShowEmailResponseModal] = useState<boolean>(false)
  const [showFormFillModal, setShowFormFillModal] = useState<boolean>(false)
  const { hasScope } = useZocksAuth()

  const manualConsentTrackingAvailable = hasQuota('identity.manualConsentTrackingEnabled.value')

  const hasManualConsentManagementAdminScope = hasScope('**', 'consent_management:admin', [])

  const { userConsentManualOverride } = useConsentManualConfigs()
  const manualConsentEditEnabled = (userConsentManualOverride || hasManualConsentManagementAdminScope) && manualConsentTrackingAvailable

  useEffect(() => {
    if (!userId) {
      return
    }
    triggerGetRoomSessions({
      accountId,
      participantId: userId,
      pageRequest: { page: paginationPage - 1, size: paginationPageSize },
    })
  }, [accountId, paginationPage, paginationPageSize, triggerGetRoomSessions, userId])

  useEffect(() => {
    const currentPage = searchParams.get('page')
    const pageSize = searchParams.get('pageSize')

    if (currentPage) {
      setPaginationPage(parseInt(currentPage, 10))
    }

    if (pageSize) {
      const size = parseInt(pageSize, 10)
      setPaginationPageSize(size)
    }
  }, [searchParams])

  const handleChangePage = (page: number) => {
    searchParams.set('page', `${page}`)
    setSearchParams(searchParams, { replace: true })
  }

  const handleChangePageSize = (value: number | undefined) => {
    if (value) {
      searchParams.set('pageSize', `${value}`)
      searchParams.set('page', '1')
      setSearchParams(searchParams, { replace: true })
    }
  }

  const handleCancelEditContactModal = () => {
    setShowEditContactModal(false)
  }

  const handleSaveEditContactModal = () => {
    setShowEditContactModal(false)
    refetchContact()
  }

  const handleShowEditContactModal = () => {
    setShowEditContactModal(true)
  }

  const handleShowAskAnythingModal = () => {
    setShowAskAnythingModal(true)
  }

  const handleCancelAskAnythingModal = () => {
    setShowAskAnythingModal(false)
  }

  const _handleShowContactMeetingPrepModal = () => {
    setShowContactMeetingPrepModal(true)
  }

  const handleShowContactMeetingPrepModal = tracked(_handleShowContactMeetingPrepModal, {
    eventName: TrackEvent.MEETING_PREP_OPENED,
    eventDetails: {
      contactId: userId,
      source: 'contact',
    },
  })

  const handleCancelContactMeetingPrepModal = () => {
    setShowContactMeetingPrepModal(false)
  }

  const handleShowEmailResponseModal = () => {
    setShowEmailResponseModal(true)
  }

  const handleCancelEmailResponseModal = () => {
    setShowEmailResponseModal(false)
  }

  const _handleShowFormFillModal = () => {
    setShowFormFillModal(true)
    dispatch(setFormFillStartedFrom('contact'))
  }

  const handleNewEmailButtonClick = () => {
    const _searchParams = { contactId: userId }
    navigate({
      pathname: '/email/new',
      search: createSearchParams(_searchParams).toString(),
    })
  }

  const handleProcessDocumentButtonClick = () => {
    navigate(`/library/manage-contacts/contacts/${userId}/documents/upload`)
  }

  const handleShowFormFillModal = tracked(_handleShowFormFillModal, {
    eventName: TrackEvent.CONTACT_FORM_FILL_OPENED,
    eventDetails: {
      contactId: userId,
    },
  })

  const handleCancelFormFillModal = () => {
    setShowFormFillModal(false)
    dispatch(setFormFillStartedFrom(null))
  }

  const handleAssistantOptionSelected = (optionId: string) => {
    if (optionId === 'ask-anything') {
      handleShowAskAnythingModal()
    } else if (optionId === 'email-response') {
      handleShowEmailResponseModal()
    } else if (optionId === 'form-fill') {
      handleShowFormFillModal()
    } else {
      handleShowContactMeetingPrepModal()
    }
  }

  const sessionCount = roomSessions?.page.totalElements ?? 0
  const getPastMeetingsBody = () => {
    if (roomSessionsLoading) {
      return (
        <div className='d-flex justify-content-center'>
          <Spinner />
        </div>
      )
    }
    const sessions = roomSessions?.data ?? []
    if (!sessions.length) {
      return <div>No meetings were found for the contact. Make sure to assign the contacts for the meetings.</div>
    }

    return (
      <>
        <RoomSessionList roomSessions={sessions} showDemoSession={false} onSessionDeleted={() => {}} />
        <Container fluid>
          <Row className='justify-content-end'>
            <Col>
              <CustomPaginationComponent
                rowsPerPage={paginationPageSize}
                rowCount={sessionCount}
                currentPage={paginationPage}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={(value) => handleChangePageSize(value)}
              />
            </Col>
          </Row>
        </Container>
      </>
    )
  }

  const [showConsentModal, setShowConsentModal] = useState(false)
  const handleClickContactConsent = () => {
    setShowConsentModal(true)
  }

  const handleCloseConsentModal = () => {
    setShowConsentModal(false)
    refetchContact()
  }

  const [showClientProfileNotAvailableModal, setShowClientProfileNotAvailableModal] = useState(false)

  const _changeTab = (key: string | null) => {
    if (key && key !== 'scorecards-upgrade-modal') {
      setSelectedTab(key)
      navigate(`/library/manage-contacts/contacts/${userId}/tabs/${key}`, { replace: true })
    } else {
      setShowClientProfileNotAvailableModal(true)
    }
  }

  const changeTab = tracked(
    _changeTab,
    { eventName: TrackEvent.CONTACT_TAB_CHANGED },
    {
      eventDetailsExtractor: (key: string | null) => {
        return { tab: key }
      },
    },
  )

  if (isLoadingUserData || !contactData?.data) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    )
  }

  const clientProfileTitle = (
    <div className='d-flex align-items-center'>
      {!clientProfileAvailable ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Client Profile
    </div>
  )

  const BusinessEventsTitle = (
    <div className='d-flex align-items-center gap-2'>
      {!businessEventsEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Client Signals
    </div>
  )

  const DocumentsTitle = (
    <div className='d-flex align-items-center gap-2'>
      {!documentSummaryEnabled ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Documents
    </div>
  )

  const isClient = contactData.data.roles.some((role) => role.name === 'Client')
  const isContactLinkedToCRM = !!contactData.data.integrations?.find(
    (i) => i.type === 'crm' || i.type === 'planning' || i.type === 'portfolio' || i.type === 'tax' || i.type === 'estate_planning',
  )

  return (
    <Container>
      <ConsentModal show={showConsentModal} onCancel={handleCloseConsentModal} contact={contactData.data} />
      <ClientProfileNotAvailableModal show={showClientProfileNotAvailableModal} onHide={() => setShowClientProfileNotAvailableModal(false)} />
      {showEditContactModal ? (
        <EditContactModal userId={userId} show={showEditContactModal} onCancel={handleCancelEditContactModal} onSave={handleSaveEditContactModal} />
      ) : null}
      {showContactMeetingPrepModal && (
        <ContactMeetingPrepModal contactId={userId} onHide={handleCancelContactMeetingPrepModal} show={showContactMeetingPrepModal} />
      )}
      {showAskAnythingModal && (
        <AskAnythingContactModal
          contactId={userId}
          show={showAskAnythingModal}
          onCancel={handleCancelAskAnythingModal}
          displayName={contactData.data.firstName ?? contactData.data.name ?? 'the client'}
        />
      )}
      {showEmailResponseModal && <ContactEmailResponseModal show={showEmailResponseModal} onCancel={handleCancelEmailResponseModal} />}
      {showFormFillModal && <FormFillModal contactId={userId} show={showFormFillModal} onHide={handleCancelFormFillModal} />}
      <Row>
        <Col className='d-flex flex-column zocks-gap-3'>
          <Row>
            <Col className='p-0 d-flex flex-column zocks-gap-3'>
              <Row>
                <Col className='p-0'>
                  <Breadcrumb>
                    <Breadcrumb.Item
                      linkProps={{
                        to: '/library/manage-contacts',
                        className: `${buttonStyles.button} ${buttonStyles.linkButton} text-decoration-underline`,
                      }}
                      linkAs={NavLink}
                    >
                      Contacts
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>{getUserDisplayName(contactData.data)}</Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
              </Row>
              <Row>
                <Col className='d-flex flex-column zocks-gap-3'>
                  <Row>
                    <Col className='p-0 d-flex align-items-center gap-3'>
                      <h3>{getUserDisplayName(contactData.data)}</h3>
                      <Button variant='grayGhost' className='px-2 py-1' onClick={handleShowEditContactModal}>
                        <i className='bi bi-pencil' />
                        <span>Edit</span>
                      </Button>
                    </Col>
                  </Row>
                  <Row className='d-flex zocks-gap-2 justify-content-between'>
                    <Col>
                      <Row className='w-100 h-100 zocks-gap-2'>
                        {consentTrackingAvailable && account?.data.effectiveConfig.complianceConfig.userConsentTrackingEnabled && (
                          <Col
                            className='ps-3 p-0 gap-1 d-flex flex-column contact-data-container w-100'
                            {...(isClient && {
                              onClick: handleClickContactConsent,
                              role: 'button',
                              tabIndex: 0,
                            })}
                          >
                            <Row className='d-flex align-items-center justify-content-start'>
                              <Col xs='auto' className='lead-small pe-0'>
                                Consent
                              </Col>
                              {manualConsentEditEnabled ? (
                                <Col xs='auto' className='ps-1'>
                                  <i className='bi bi-pencil-square' />
                                </Col>
                              ) : (
                                <Col xs='auto' className='ps-1'>
                                  <i className='bi bi-chevron-right' />
                                </Col>
                              )}
                            </Row>
                            {isClient ? (
                              <ConsentIcon contact={contactData.data} />
                            ) : (
                              <span className='paragraph-small zocks-text-gray-600'>Consent is only tracked for clients.</span>
                            )}
                          </Col>
                        )}
                        <Col className='ps-3 p-0 gap-1 d-flex flex-column contact-data-container w-100'>
                          <div className='lead-small'>Email</div>
                          <div className='paragraph-medium'>{contactData.data.email}</div>
                        </Col>
                        <Col className='ps-3 p-0 gap-1 d-flex flex-column contact-data-container w-100'>
                          <div className='lead-small'>Role</div>
                          <div className='paragraph-medium'>
                            <Roles userRoleIds={contactData.data.roles.map((role) => role.id)} />
                          </div>
                        </Col>
                        <Col className='ps-3 p-0 gap-1 d-flex flex-column contact-data-container w-100'>
                          <div className='lead-small'>Language</div>
                          <div className='paragraph-medium'>
                            {contactData.data.settings?.language ? getContactLabel(contactData.data.settings.language) : 'Not Set'}
                          </div>
                        </Col>
                        <Col className='ps-3 p-0 gap-1 d-flex flex-column contact-data-container w-100'>
                          <div className='lead-small'>Owner</div>
                          <div className='paragraph-medium'>{contactOwner ? getUserDisplayName(contactOwner) : 'Not Set'}</div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row>
                    <Col className='d-flex'>
                      <Button
                        className={classNames(
                          'd-flex align-items-center justify-content-start flex-grow-1 px-3 zocks-py-0.75 zocks-gap-1 border rounded-3 position-relative',
                          {
                            'zocks-border-color-gray-500 cursor-pointer': documentSummaryEnabled,
                            'zocks-border-color-primary': !documentSummaryEnabled,
                          },
                        )}
                        onClick={handleProcessDocumentButtonClick}
                      >
                        {!documentSummaryEnabled ? <ContactActionUpgradeBadge /> : null}
                        <div className='contact-action-icon-wrapper d-flex align-items-center justify-content-center flex-shrink-0'>
                          <i className='bi bi-file-earmark-richtext' />
                        </div>
                        <div className='d-flex flex-column'>
                          <span className='paragraph-medium bold'>Document Intelligence</span>
                          <span className='paragraph-small'>Extract insights from documents</span>
                        </div>
                      </Button>
                    </Col>
                    <Col className='d-flex'>
                      <Button
                        className={classNames(
                          'd-flex align-items-center justify-content-start flex-grow-1 px-3 zocks-py-0.75 zocks-gap-1 border rounded-3 position-relative',
                          {
                            'zocks-border-color-gray-500 cursor-pointer': emailEnabled,
                            'zocks-border-color-primary': !emailEnabled,
                          },
                        )}
                        onClick={handleNewEmailButtonClick}
                      >
                        {!emailEnabled ? <ContactActionUpgradeBadge /> : null}
                        <div className='contact-action-icon-wrapper d-flex align-items-center justify-content-center flex-shrink-0'>
                          <i className='bi bi-envelope' />
                        </div>
                        <div className='d-flex flex-column'>
                          <span className='paragraph-medium bold'>Email generation</span>
                          <span className='paragraph-small'>Draft personalized contact emails</span>
                        </div>
                      </Button>
                    </Col>
                    <Col>
                      <AIAssistantDropdown
                        options={[
                          {
                            id: 'ask-anything',
                            label: 'Ask Anything',
                            disabled: !sessionCount && !isContactLinkedToCRM,
                            tooltipTitle: 'After you have a meeting with this contact or link them to CRM, you can ask anything',
                          },
                          {
                            id: 'meeting-prep',
                            label: 'Meeting Preparation',
                          },
                          {
                            id: 'email-response',
                            label: 'E-mail reply',
                          },
                          {
                            id: 'form-fill',
                            label: 'Form Fill',
                            disabled: !formEnabled || !isClient,
                            tooltipTitle: !formEnabled ? 'Upgrade to unlock this feature' : !isClient ? 'Only available for clients' : undefined,
                          },
                        ]}
                        onOptionSelected={handleAssistantOptionSelected}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col className='p-0'>
              <Row>
                <Col className='p-0'>
                  <Tabs activeKey={selectedTab} id='practifi-settings' className='zocks-mb-2.5 mt-4' onSelect={changeTab} mountOnEnter>
                    <Tab eventKey='pastMeetings' title={<>Past meetings{sessionCount ? ` (${sessionCount})` : null}</>}>
                      {getPastMeetingsBody()}
                    </Tab>
                    {emailEnabled && emailIntegrations.length ? (
                      <Tab eventKey='emails' title='Emails'>
                        <ContactEmailsV2 contact={contactData.data} />
                      </Tab>
                    ) : null}
                    <Tab key='tasks' eventKey='tasks' title='Tasks'>
                      <ContactTasks contact={contactData.data} />
                    </Tab>
                    {!clientProfileAvailable ? (
                      <Tab tabClassName='disabled upgrade-trigger-tab' eventKey='scorecards-upgrade-modal' title={clientProfileTitle}>
                        <></>
                      </Tab>
                    ) : (
                      <Tab className='mt-5' eventKey='scorecards' title={clientProfileTitle} disabled={!isClient} mountOnEnter>
                        <ClientProfile contact={contactData.data} />
                      </Tab>
                    )}
                    <Tab eventKey='businessEvents' title={BusinessEventsTitle} disabled={!isClient}>
                      <ContactBusinessEvents contact={contactData.data} />
                    </Tab>
                    <Tab key='insights' eventKey='insights' title='Analytics'>
                      <ContactAnalytics userId={contactData.data.id} />
                    </Tab>
                    <Tab eventKey='documents' title={DocumentsTitle} disabled={!isClient}>
                      <ContactDocuments contact={contactData.data} />
                    </Tab>
                  </Tabs>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  )
}

export const ContactActionUpgradeBadge = () => {
  return (
    <div className='d-flex align-items-center contact-action-upgrade-badge rounded-start rounded-top zocks-bg-color-primary zocks-gap-0.25 zocks-py-0.25 px-2'>
      <div className='d-flex align-items-center justify-content-center flex-shrink-0 contact-action-upgrade-badge-icon-wrapper'>
        <i className='bi bi-star-fill text-white' />
      </div>
      <span className='lead-extra-small text-white'>Upgrade</span>
    </div>
  )
}
